import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  BookOpen, 
  Clock, 
  Users, 
  TrendingUp,
  Play,
  Calendar,
  ArrowRight,
  Edit
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLessonContext } from '../../contexts/LessonContext';
import { useTranslation } from '../../contexts/TranslationContext';
import { useAuth } from '../../contexts/AuthContext';
import { lessonService } from '../../services/lessonService';
import { studentService } from '../../services/studentService';
import { trackingService } from '../../services/trackingService';
import { Lesson } from '../../types';

const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  change?: string;
}> = ({ title, value, icon, color, change }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="card"
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        {change && (
          <p className="text-sm text-green-600 mt-1 flex items-center">
            <TrendingUp className="w-4 h-4 mr-1" />
            {change}
          </p>
        )}
      </div>
      <div className={`p-3 rounded-lg ${color}`}>
        {icon}
      </div>
    </div>
  </motion.div>
);

const RecentLessonCard: React.FC<{ lesson: Lesson }> = ({ lesson }) => {
  const { t } = useTranslation();
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className="card hover:shadow-md transition-shadow cursor-pointer"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 mb-1">{lesson.metadata.title}</h3>
          <p className="text-sm text-gray-600 mb-2">
          {lesson.metadata.subject} • {t('lesson.grade')} {lesson.metadata.grade}
          {lesson.metadata.subconstruct && ` • ${lesson.metadata.subconstruct}`}
          {lesson.className && ` • ${lesson.className}`}
        </p>
          <div className="flex items-center space-x-4 text-xs text-gray-500">
            <span className="flex items-center">
              <Clock className="w-3 h-3 mr-1" />
              {lesson.metadata.duration_minutes} {t('lessonList.min')}
            </span>
            <span className="flex items-center">
              <BookOpen className="w-3 h-3 mr-1" />
              {lesson.timeline.length} {t('lessonList.steps')}
            </span>
          </div>
        </div>
        <div className="flex items-center space-x-1">
          <Link
            to={`/lessons/${lesson.metadata.lesson_id}/edit`}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            title={t('lessonList.edit')}
          >
            <Edit className="w-4 h-4 text-gray-400" />
          </Link>
          <Link
            to={`/session/${lesson.metadata.lesson_id}`}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            title={t('lessonList.start')}
          >
            <ArrowRight className="w-4 h-4 text-gray-400" />
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

export const Dashboard: React.FC = () => {
  const { state, dispatch } = useLessonContext();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalLessons: 0,
    completedSessions: 0,
    totalStudents: 0,
    averageRating: 0,
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.id) return;
      
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        const [lessons, students] = await Promise.all([
          lessonService.getAllLessons(user.id),
          studentService.getStudents(user.id),
        ]);
        dispatch({ type: 'SET_LESSONS', payload: lessons });

        const lessonSummaries = await Promise.all(lessons.map((lesson: Lesson) => trackingService.getLessonSummary(lesson.metadata.lesson_id)));
        const averageRating = lessonSummaries.length > 0 
          ? lessonSummaries.reduce((acc: number, curr: any) => acc + curr.averageScore, 0) / lessonSummaries.length 
          : 0;
        
        // Calculate stats for the current teacher
        setStats({
          totalLessons: lessons.length,
          completedSessions: Math.floor(lessons.length * 0.7),
          totalStudents: students.length,
          averageRating: parseFloat((averageRating * 5 / 100).toFixed(1)),
        });
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to fetch lessons' });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    fetchData();
  }, [dispatch, user?.id]);

  const recentLessons = state.lessons.slice(0, 5);

  // Show loading state if user is not available
  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('dashboard.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card bg-gradient-to-r from-primary-600 to-primary-700 text-white"
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">{t('dashboard.welcomeBack')}, {user.name}!</h1>
            <p className="text-primary-100">
              {t('dashboard.readyToInspire').replace('{count}', state.lessons.length.toString())}
            </p>
          </div>
          <div className="hidden md:block">
            <Play className="w-16 h-16 text-primary-200" />
          </div>
        </div>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title={t('dashboard.totalLessons')}
          value={stats.totalLessons}
          icon={<BookOpen className="w-6 h-6 text-white" />}
          color="bg-blue-500"
          change={`+12% ${t('dashboard.thisMonth')}`}
        />
        <StatCard
          title={t('dashboard.completedSessions')}
          value={stats.completedSessions}
          icon={<Clock className="w-6 h-6 text-white" />}
          color="bg-green-500"
          change={`+8% ${t('dashboard.thisWeek')}`}
        />
        <StatCard
          title={t('dashboard.totalStudents')}
          value={stats.totalStudents}
          icon={<Users className="w-6 h-6 text-white" />}
          color="bg-purple-500"
        />
        <StatCard
          title={t('dashboard.averageRating')}
          value={stats.averageRating}
          icon={<TrendingUp className="w-6 h-6 text-white" />}
          color="bg-orange-500"
          change={`+0.2 ${t('dashboard.thisMonth')}`}
        />
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card"
      >
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('dashboard.quickActions')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link
            to="/lessons"
            className="flex items-center space-x-3 p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-all group"
          >
            <div className="p-2 bg-primary-100 rounded-lg group-hover:bg-primary-200 transition-colors">
              <BookOpen className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">{t('dashboard.browseLessons')}</p>
              <p className="text-sm text-gray-500">{t('dashboard.findNextLesson')}</p>
            </div>
          </Link>
          
          <Link
            to="/session"
            className="flex items-center space-x-3 p-4 rounded-lg border border-gray-200 hover:border-green-300 hover:bg-green-50 transition-all group"
          >
            <div className="p-2 bg-green-100 rounded-lg group-hover:bg-green-200 transition-colors">
              <Play className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">{t('dashboard.startSession')}</p>
              <p className="text-sm text-gray-500">{t('dashboard.beginTeaching')}</p>
            </div>
          </Link>
          
          <Link
            to="/schedule"
            className="flex items-center space-x-3 p-4 rounded-lg border border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-all group"
          >
            <div className="p-2 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-colors">
              <Calendar className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">{t('dashboard.viewSchedule')}</p>
              <p className="text-sm text-gray-500">{t('dashboard.checkCalendar')}</p>
            </div>
          </Link>
          
          <Link
            to="/analytics"
            className="flex items-center space-x-3 p-4 rounded-lg border border-gray-200 hover:border-orange-300 hover:bg-orange-50 transition-all group"
          >
            <div className="p-2 bg-orange-100 rounded-lg group-hover:bg-orange-200 transition-colors">
              <TrendingUp className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">{t('dashboard.analytics')}</p>
              <p className="text-sm text-gray-500">{t('dashboard.viewProgress')}</p>
            </div>
          </Link>
        </div>
      </motion.div>

      {/* Recent Lessons */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">{t('dashboard.recentLessons')}</h2>
          <Link
            to="/lessons"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            {t('dashboard.viewAll')}
          </Link>
        </div>
        
        {state.loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-20 bg-gray-200 rounded-lg"></div>
              </div>
            ))}
          </div>
        ) : recentLessons.length > 0 ? (
          <div className="space-y-4">
            {recentLessons.map((lesson) => (
              <RecentLessonCard key={lesson.metadata.lesson_id} lesson={lesson} />
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">{t('dashboard.noLessonsYet')}</p>
            <Link
              to="/lessons"
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              {t('dashboard.createFirstLesson')}
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  );
};
